import { fetchApi } from '../client';
import { secureStorage } from '../../secureStorage';
import { captureStaffSession } from '../sessionToken';
import {
  hashPasswordForTransmission,
  normalizeCredentials,
  PASSWORD_HASH_HEADER,
  PASSWORD_HASH_VALUE,
} from '../../loginSecurity';
import type { User, Clinic } from '../../../types';

export interface AccessibleClinic {
  clinicId: string;
  clinicName: string;
  city: string | null;
  state: string | null;
  branchCode: string | null;
  tenantType: 'INDEPENDENT_CLINIC' | 'BRANCH_CLINIC';
  hospitalGroupId: string | null;
  roleInClinic: string | null;
  isPrimary: boolean;
  status: string;
}

async function doLogin(
  email: string,
  password: string,
  extraHeaders?: Record<string, string>,
) {
  return fetchApi<{ token: string; user: User; clinic: Clinic }>('/api/auth/login', {
    method: 'POST',
    skipBodyLog: true,
    body: JSON.stringify({ email, password }),
    headers: extraHeaders,
  });
}

export const authModule = {
  login: async (rawEmail: string, rawPassword: string) => {
    const { email, password } = normalizeCredentials(rawEmail, rawPassword);

    // Attempt login with PBKDF2-hashed password (defense-in-depth over TLS)
    let pbkdf2Hash: string | null = null;
    try {
      pbkdf2Hash = await hashPasswordForTransmission(password, email);
    } catch {
      // WebCrypto unavailable (very old browser) — fall through to plaintext
    }

    const response = await doLogin(
      email,
      pbkdf2Hash ?? password,
      pbkdf2Hash ? { [PASSWORD_HASH_HEADER]: PASSWORD_HASH_VALUE } : undefined,
    );

    // MFA-enabled account: password was correct, but no session exists yet, so there is no token to
    // capture — captureStaffSession returns null and stores nothing. The caller (AuthContext)
    // checks responseData.mfaRequired and routes to the code-entry step.
    captureStaffSession(response);
    return response;
  },

  /**
   * Exchanges the httpOnly refresh_token cookie (sent automatically via credentials:'include')
   * for a new access token, silently extending the session without a full re-login. Mirrors
   * login()'s token-capture above — the single source of truth every other part of the app reads
   * (WebSocket, raw fetch call sites) is secureStorage, so updating it here is enough to keep all
   * of them current.
   */
  refresh: async () => {
    const response = await fetchApi<{ token: string }>('/api/auth/refresh', { method: 'POST' });
    captureStaffSession(response);
    return response;
  },

  logout: async () => {
    try {
      const response = await fetchApi('/api/auth/logout', { method: 'POST' });
      secureStorage.removeAuthToken();
      return response;
    } catch {
      secureStorage.removeAuthToken();
      return { data: null };
    }
  },

  getCurrentUser: () =>
    fetchApi<{ user: User; clinic: Clinic; data?: { user: User; clinic: Clinic } }>('/api/auth/me'),

  /**
   * Mints a single-use, short-lived ticket for opening the STOMP/WebSocket connection. The web client
   * is cookie-only (no JWT in JS), and the STOMP CONNECT frame can't carry the httpOnly cookie, so it
   * presents this ticket instead. Fetch a fresh one immediately before each connect/reconnect.
   */
  getWebSocketTicket: () =>
    fetchApi<{ ticket: string }>('/api/auth/ws-ticket'),

  /** Lists the user's active login sessions (one per device/browser) for the Active Sessions panel. */
  getSessions: () =>
    fetchApi<Array<{
      sessionId: string;
      ipAddress: string | null;
      userAgent: string | null;
      signedInAt: string | null;
      lastActiveAt: string | null;
      current: boolean;
    }>>('/api/auth/sessions'),

  /** Signs out of every device: revokes all refresh families + invalidates all access tokens. */
  logoutAllDevices: () =>
    fetchApi<{ message: string }>('/api/auth/logout-all', { method: 'POST' }),

  /** Revokes one specific session from the Active Sessions list. Rejected for the caller's own
   *  current session — use logout() for that instead. */
  revokeSession: (sessionId: string) =>
    fetchApi<{ message: string }>(`/api/auth/sessions/${sessionId}`, { method: 'DELETE' }),

  // -----------------------------------------------------------------------
  // Multi-branch tenant APIs (V2026.05.16)
  // -----------------------------------------------------------------------

  /** Returns all clinics the authenticated user has active access to. */
  getMyClinics: () =>
    fetchApi<AccessibleClinic[]>('/api/v1/auth/my-clinics'),

  /** Validates that the user has access to a specific clinic before switching to it. */
  validateClinicAccess: (clinicId: string) =>
    fetchApi<{ allowed: boolean; clinicId: string; clinicName: string }>(
      `/api/v1/auth/validate-clinic-access/${clinicId}`
    ),

  /** Returns the server-resolved TenantContext for the current request. Useful for debugging. */
  getTenantContext: () =>
    fetchApi<Record<string, unknown>>('/api/v1/auth/tenant-context'),

  verifyInvitation: (token: string) =>
    fetchApi('/api/auth/invitation/verify', {
      method: 'POST',
      skipAuth: true,
      body: JSON.stringify({ token }),
    }),

  acceptInvitation: async (data: { token: string; password: string; email: string; termsAccepted?: boolean; termsVersion?: string }) => {
    const { email, password, ...rest } = data;
    let finalPassword = password;
    let extraHeaders: Record<string, string> | undefined;
    try {
      finalPassword = await hashPasswordForTransmission(password, email);
      extraHeaders = { [PASSWORD_HASH_HEADER]: PASSWORD_HASH_VALUE };
    } catch {
      // WebCrypto unavailable — send plaintext as fallback
    }
    return fetchApi('/api/auth/invitation/accept', {
      method: 'POST',
      skipAuth: true,
      skipBodyLog: true,
      body: JSON.stringify({ password: finalPassword, ...rest }),
      headers: extraHeaders,
    });
  },

  requestPasswordReset: (data: { email: string }) =>
    fetchApi<{ message: string }>('/api/auth/password/forgot', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  verifyPasswordResetToken: (token: string) =>
    fetchApi<any>('/api/auth/password/verify', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),

  resetPassword: async (data: { token: string; password: string; email: string }) => {
    const { email, password, token } = data;
    let finalPassword = password;
    let extraHeaders: Record<string, string> | undefined;
    try {
      finalPassword = await hashPasswordForTransmission(password, email);
      extraHeaders = { [PASSWORD_HASH_HEADER]: PASSWORD_HASH_VALUE };
    } catch {
      // WebCrypto unavailable — send plaintext as fallback
    }
    return fetchApi<{ message: string }>('/api/auth/password/reset', {
      method: 'POST',
      skipBodyLog: true,
      body: JSON.stringify({ token, password: finalPassword }),
      headers: extraHeaders,
    });
  },

  changePassword: async (email: string, oldPassword: string, newPassword: string, confirmPassword: string) => {
    let finalOld = oldPassword;
    let finalNew = newPassword;
    let finalConfirm = confirmPassword;
    let extraHeaders: Record<string, string> | undefined;
    try {
      finalOld = await hashPasswordForTransmission(oldPassword, email);
      finalNew = await hashPasswordForTransmission(newPassword, email);
      finalConfirm = finalNew;
      extraHeaders = { [PASSWORD_HASH_HEADER]: PASSWORD_HASH_VALUE };
    } catch {
      // WebCrypto unavailable — send plaintext as fallback
    }
    return fetchApi<{ message: string }>('/api/auth/change-password', {
      method: 'POST',
      skipBodyLog: true,
      body: JSON.stringify({ oldPassword: finalOld, newPassword: finalNew, confirmPassword: finalConfirm }),
      headers: extraHeaders,
    });
  },

  getMyPermissions: () =>
    fetchApi<{
      userId: string;
      clinicId: string;
      role: string;
      isSuperAdmin: boolean;
      permissions: string[];
      enabledModules: string[];
      visiblePaths?: string[] | null;
      planType?: string | null;
      subscriptionStatus?: string | null;
      data?: {
        userId: string;
        clinicId: string;
        role: string;
        isSuperAdmin: boolean;
        permissions: string[];
        enabledModules: string[];
        visiblePaths?: string[] | null;
        planType?: string | null;
        subscriptionStatus?: string | null;
      };
    }>('/api/auth/my-permissions'),
};
